// =====================================================================
//  LA FECHA DÍA/MES/AÑO  (D231, sesión 176)
// =====================================================================
//  El `<input type="date">` muestra la fecha como dice el IDIOMA DEL
//  NAVEGADOR, no la página: en un Chrome en inglés, el 3 de noviembre se ve
//  11/03/2026. Steven pidió (3/11) día/mes/año siempre, para todos.
//
//  Esto convierte cada `input[type=date]` (y el `datetime-local` de Precios)
//  en un campo de texto `dd/mm/aaaa` (`dd/mm/aaaa hh:mm`) con un calendario
//  que se abre al tocarlo. Lo hace EN EL MISMO ELEMENTO, con el mismo `id`,
//  y su `.value` sigue hablando como antes: se lee `aaaa-mm-dd` (o
//  `aaaa-mm-ddThh:mm`) y se le asigna lo mismo. Así ninguna pantalla cambia,
//  y la base recibe lo mismo de siempre.
//    · Lo que se escribe a medias (o una fecha que no existe) se lee como
//      vacío, igual que el campo de fecha del navegador; el campo se marca
//      en rojo y dice cómo escribirla.
//    · También se acepta `aaaa-mm-dd` escrito, y `3/11/2026`: al salir del
//      campo queda `03/11/2026`.
//    · El `change` llega como con el campo del navegador: apenas lo escrito
//      pasa a ser otra fecha entera (o se borra), sin esperar a salir del
//      campo; elegir en el calendario, igual. El `change` propio del campo de
//      texto (al salir) se calla: llegaría dos veces.
//    · `min` y `max` (Vacaciones: el «hasta» no antes del «desde») se
//      respetan: el calendario no ofrece esos días y lo escrito fuera de
//      rango deja el campo inválido, así que el formulario no sale, como
//      antes.
//  Se arma solo al arrancar y con cada campo que una pantalla agregue
//  después (Vacaciones y Vueltas arman los suyos con innerHTML).

const PROTO = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')!;
const ZONA = 'America/Argentina/Buenos_Aires';
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto',
  'septiembre', 'octubre', 'noviembre', 'diciembre'];
const DIAS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

const dos = (n: number) => String(n).padStart(2, '0');
const existe = (a: number, m: number, d: number) => {
  if (a < 1900 || a > 2200 || m < 1 || m > 12 || d < 1) return false;
  return d <= new Date(Date.UTC(a, m, 0)).getUTCDate();
};
const horaBien = (h: number, mi: number) => h >= 0 && h <= 23 && mi >= 0 && mi <= 59;

//  Lo escrito → lo que lee la pantalla (`aaaa-mm-dd` o `aaaa-mm-ddThh:mm`), o
//  '' si no es una fecha entera.
export function aIso(texto: string, conHora: boolean): string {
  const t = (texto || '').trim();
  if (!t) return '';
  let a: number, m: number, d: number, h = 0, mi = 0, hayHora = false;
  let r = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ](\d{1,2}):(\d{2}))?$/);
  if (r){ a = +r[1]; m = +r[2]; d = +r[3]; if (r[4] != null){ hayHora = true; h = +r[4]; mi = +r[5]; } }
  else {
    r = t.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})(?:,?\s+(\d{1,2}):(\d{2}))?$/);
    if (!r) return '';
    d = +r[1]; m = +r[2]; a = +r[3];
    if (r[4] != null){ hayHora = true; h = +r[4]; mi = +r[5]; }
  }
  if (!existe(a, m, d)) return '';
  const f = `${a}-${dos(m)}-${dos(d)}`;
  if (!conHora) return hayHora ? '' : f;
  if (!hayHora || !horaBien(h, mi)) return '';
  return `${f}T${dos(h)}:${dos(mi)}`;
}

//  Lo que lee la pantalla → lo que se muestra.
export function aTexto(iso: string, conHora: boolean): string {
  const v = String(iso ?? '').trim();
  if (!v) return '';
  const r = v.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
  if (!r) return v;
  const f = `${r[3]}/${r[2]}/${r[1]}`;
  return conHora && r[4] != null ? `${f} ${r[4]}:${r[5]}` : f;
}

const hoyIso = () => new Intl.DateTimeFormat('en-CA', { timeZone: ZONA, year: 'numeric',
  month: '2-digit', day: '2-digit' }).format(new Date());
const ahoraHora = () => new Intl.DateTimeFormat('es-AR', { timeZone: ZONA, hour: '2-digit',
  minute: '2-digit', hour12: false }).format(new Date());

// ---------- el calendario (uno solo para toda la página) ----------
let CAL: HTMLElement | null = null;
let DE: HTMLInputElement | null = null;     // el campo al que pertenece
let MES_VISTO = { a: 2026, m: 1 };

function cerrarCalendario(){
  if (!CAL) return;
  CAL.remove();
  CAL = null;
  if (DE) DE.setAttribute('aria-expanded', 'false');
  DE = null;
}

function pintarCalendario(){
  if (!CAL || !DE) return;
  const conHora = DE.dataset.fecha === 'hora';
  const elegido = DE.value.slice(0, 10);
  const hoy = hoyIso();
  const min = aIso(DE.getAttribute('min') || '', false), max = aIso(DE.getAttribute('max') || '', false);
  const { a, m } = MES_VISTO;
  const primero = (new Date(Date.UTC(a, m - 1, 1)).getUTCDay() + 6) % 7;   // lunes = 0
  const cuantos = new Date(Date.UTC(a, m, 0)).getUTCDate();
  let dias = '';
  for (let i = 0; i < primero; i++) dias += '<span></span>';
  for (let d = 1; d <= cuantos; d++){
    const iso = `${a}-${dos(m)}-${dos(d)}`;
    const fuera = (min && iso < min) || (max && iso > max);
    dias += `<button type="button" data-dia="${iso}"${fuera ? ' disabled' : ''}` +
      `${iso === elegido ? ' aria-pressed="true"' : ''}${iso === hoy ? ' class="hoy"' : ''}` +
      ` aria-label="${d} de ${MESES[m - 1]} de ${a}">${d}</button>`;
  }
  CAL.innerHTML = `
    <div class="cal-cab">
      <button type="button" data-mover="-1" aria-label="Mes anterior">‹</button>
      <b>${MESES[m - 1]} ${a}</b>
      <button type="button" data-mover="1" aria-label="Mes siguiente">›</button>
    </div>
    <div class="cal-sem">${DIAS.map(x => `<span>${x}</span>`).join('')}</div>
    <div class="cal-dias">${dias}</div>
    <div class="cal-pie">
      <button type="button" data-dia="${hoy}">Hoy</button>
      ${DE.required ? '' : '<button type="button" data-borrar>Borrar</button>'}
      ${conHora ? '<span class="hint">la hora se escribe en el campo</span>' : ''}
    </div>`;
}

function elegir(iso: string){
  const inp = DE;
  if (!inp) return;
  let v = iso;
  if (inp.dataset.fecha === 'hora'){
    const antes = inp.value;
    v = `${iso}T${antes ? antes.slice(11, 16) : ahoraHora()}`;
  }
  PROTO.set!.call(inp, aTexto(v, inp.dataset.fecha === 'hora'));
  inp.dispatchEvent(new Event('input', { bubbles: true }));
  cerrarCalendario();
  inp.focus();
}

function abrirCalendario(inp: HTMLInputElement){
  if (DE === inp) return;
  cerrarCalendario();
  if (inp.disabled || inp.readOnly) return;
  DE = inp;
  const base = inp.value || hoyIso();
  MES_VISTO = { a: +base.slice(0, 4), m: +base.slice(5, 7) };
  CAL = document.createElement('div');
  CAL.className = 'cal';
  CAL.setAttribute('role', 'dialog');
  CAL.setAttribute('aria-label', 'Calendario');
  //  Que tocar el calendario no le saque el foco al campo (si no, el
  //  «change» del campo correría antes de elegir).
  CAL.addEventListener('mousedown', e => e.preventDefault());
  CAL.addEventListener('click', e => {
    const b = (e.target as Element).closest('button') as HTMLButtonElement | null;
    if (!b || b.disabled) return;
    if (b.dataset.mover){
      let { a, m } = MES_VISTO;
      m += Number(b.dataset.mover);
      if (m < 1){ m = 12; a--; } if (m > 12){ m = 1; a++; }
      MES_VISTO = { a, m };
      pintarCalendario();
    } else if (b.dataset.dia) elegir(b.dataset.dia);
    else if (b.hasAttribute('data-borrar') && DE){
      const inp = DE;
      PROTO.set!.call(inp, '');
      inp.dispatchEvent(new Event('input', { bubbles: true }));
      cerrarCalendario();
      inp.focus();
    }
  });
  (inp.parentElement as HTMLElement).appendChild(CAL);
  inp.setAttribute('aria-expanded', 'true');
  pintarCalendario();
}

document.addEventListener('mousedown', e => {
  if (!CAL || !DE) return;
  const t = e.target as Node;
  if (CAL.contains(t) || DE.parentElement?.contains(t)) return;
  cerrarCalendario();
}, true);
document.addEventListener('keydown', e => { if (e.key === 'Escape' && CAL) cerrarCalendario(); });

// ---------- el campo ----------
const MENSAJE = (conHora: boolean) => conHora
  ? 'Escribí la fecha y la hora como día/mes/año hora:minutos, por ejemplo 03/11/2026 14:30.'
  : 'Escribí la fecha como día/mes/año, por ejemplo 03/11/2026.';

//  En rojo si hay algo escrito que no es una fecha entera, o si está fuera
//  de `min`/`max`.
function marcarMal(inp: HTMLInputElement){
  const conHora = inp.dataset.fecha === 'hora';
  const crudo = PROTO.get!.call(inp) as string;
  const v = aIso(crudo, conHora);
  const min = aIso(inp.getAttribute('min') || '', conHora), max = aIso(inp.getAttribute('max') || '', conHora);
  const msj = crudo.trim() && !v ? MENSAJE(conHora)
    : v && min && v < min ? `La fecha no puede ser anterior al ${aTexto(min, conHora)}.`
    : v && max && v > max ? `La fecha no puede ser posterior al ${aTexto(max, conHora)}.` : '';
  inp.classList.toggle('fecha-mal', !!msj);
  inp.setCustomValidity(msj);
  inp.title = msj;
}

export function campoFecha(inp: HTMLInputElement){
  if (inp.dataset.fecha) return;
  const conHora = inp.type === 'datetime-local';
  const inicial = inp.value;
  const atributo = inp.getAttribute('value');
  inp.type = 'text';
  inp.dataset.fecha = conHora ? 'hora' : 'dia';
  inp.inputMode = 'numeric';
  inp.autocomplete = 'off';
  inp.placeholder = conHora ? 'dd/mm/aaaa hh:mm' : 'dd/mm/aaaa';
  inp.maxLength = conHora ? 16 : 10;
  inp.classList.add('campo-fecha');
  inp.setAttribute('aria-haspopup', 'dialog');
  inp.setAttribute('aria-expanded', 'false');
  //  El `.value` de este campo habla como el de fecha del navegador. Lo que
  //  asigna el código no dispara `change` (tampoco en el del navegador).
  let ultimo = '';
  Object.defineProperty(inp, 'value', {
    configurable: true,
    get(){ return aIso(PROTO.get!.call(this), conHora); },
    set(v: string){ PROTO.set!.call(this, aTexto(v, conHora)); ultimo = this.value; marcarMal(this); },
  });
  //  El `change` de salir del campo se calla: el bueno sale de `input`.
  inp.addEventListener('change', e => { if (e.isTrusted) e.stopImmediatePropagation(); }, true);
  new MutationObserver(() => marcarMal(inp)).observe(inp, { attributes: true, attributeFilter: ['min', 'max'] });
  if (atributo != null) inp.setAttribute('value', aTexto(atributo, conHora));
  PROTO.set!.call(inp, aTexto(inicial, conHora));
  ultimo = inp.value;

  //  El campo y su botón de calendario van juntos.
  const caja = document.createElement('span');
  caja.className = 'fecha-caja';
  inp.parentElement!.insertBefore(caja, inp);
  caja.appendChild(inp);
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'fecha-cal';
  b.tabIndex = -1;
  b.setAttribute('aria-label', 'Abrir el calendario');
  b.addEventListener('click', e => {
    e.preventDefault();
    if (DE === inp) cerrarCalendario(); else { abrirCalendario(inp); inp.focus(); }
  });
  caja.appendChild(b);

  inp.addEventListener('click', () => abrirCalendario(inp));
  //  Las barras se ponen solas al escribir los números.
  inp.addEventListener('input', e => {
    if ((e as InputEvent).inputType === 'insertText'){
      const crudo = PROTO.get!.call(inp) as string;
      let nuevo = crudo;
      if (/^\d{2}$/.test(crudo) || /^\d{1,2}\/\d{2}$/.test(crudo)) nuevo = crudo + '/';
      else if (conHora && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(crudo)) nuevo = crudo + ' ';
      else if (conHora && /^\d{1,2}\/\d{1,2}\/\d{4} \d{2}$/.test(crudo)) nuevo = crudo + ':';
      if (nuevo !== crudo) PROTO.set!.call(inp, nuevo);
    }
    marcarMalSiCompleto(inp);
    if (DE === inp && inp.value){
      MES_VISTO = { a: +inp.value.slice(0, 4), m: +inp.value.slice(5, 7) };
      pintarCalendario();
    }
    //  Otra fecha entera (o vacío): el `change`, como el del navegador.
    const v = inp.value;
    if (v !== ultimo){
      ultimo = v;
      inp.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
  inp.addEventListener('keydown', e => {
    if (e.key === 'Tab') cerrarCalendario();
    if (e.key === 'ArrowDown' && e.altKey) abrirCalendario(inp);
  });
  //  Al salir, lo escrito queda con su forma (`3/11/2026` → `03/11/2026`) o
  //  en rojo si no es una fecha.
  inp.addEventListener('blur', () => {
    const v = inp.value;
    if (v) PROTO.set!.call(inp, aTexto(v, conHora));
    marcarMal(inp);
  });
}

//  Mientras se escribe no se marca en rojo lo que todavía está a medias.
function marcarMalSiCompleto(inp: HTMLInputElement){
  const crudo = (PROTO.get!.call(inp) as string).trim();
  const largo = inp.dataset.fecha === 'hora' ? 16 : 10;
  if (crudo.length >= largo || !crudo) marcarMal(inp);
  else { inp.classList.remove('fecha-mal'); inp.setCustomValidity(''); inp.title = ''; }
}

export function activarFechas(){
  const buscar = (raiz: ParentNode) =>
    raiz.querySelectorAll<HTMLInputElement>('input[type=date], input[type=datetime-local]').forEach(campoFecha);
  buscar(document);
  new MutationObserver(cambios => {
    for (const c of cambios)
      c.addedNodes.forEach(n => {
        if (n instanceof HTMLInputElement && (n.type === 'date' || n.type === 'datetime-local')) campoFecha(n);
        else if (n instanceof Element) buscar(n);
      });
  }).observe(document.body, { childList: true, subtree: true });
}
